import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Rating,
    Chip,
    TextField,
} from "@mui/material";
import { MAIN_COLOR_RED } from "../assets/styles";

export interface FeedbackDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (payload: { rating: number; labels: string[]; comment: string }) => Promise<void> | void;
}

const FEEDBACK_LABELS = [
    "Easy to use",
    "Good design",
    "Helpful",
    "Intuitive",
    "Too slow",
    "Confusing",
    "Too complex",
    "Unneeded",
];

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose, onSubmit }) => {
    const [rating, setRating] = useState<number | null>(0);
    const [labels, setLabels] = useState<string[]>([]);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleToggleLabel = (label: string) => {
        setLabels((prev) =>
            prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
        );
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await onSubmit({
                rating: rating ?? 0,
                labels,
                comment,
            });
            // Reset local state after successful submit
            setRating(0);
            setLabels([]);
            setComment("");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    backgroundColor: MAIN_COLOR_RED,
                    color: "#fff",
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    borderBottom: "none",
                    fontWeight: "bold",
                }}
            >
                Please give us your feedback!
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: 1,
                    pb: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Rating
                    name="shopping-feedback-rating"
                    value={rating}
                    onChange={(_, value) => setRating(value)}
                    max={5}
                    sx={{
                        "& .MuiRating-iconEmpty": { color: "#fff" },
                        "& .MuiRating-iconFilled": { color: "#fff" },
                    }}
                />
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    {FEEDBACK_LABELS.map((label) => {
                        const selected = labels.includes(label);
                        return (
                            <Chip
                                key={label}
                                label={label}
                                onClick={() => handleToggleLabel(label)}
                                variant={selected ? "filled" : "outlined"}
                                sx={{
                                    borderRadius: 999,
                                    borderColor: "#fff",
                                    color: selected ? MAIN_COLOR_RED : "#fff",
                                    backgroundColor: selected ? "#fff" : "transparent",
                                    "&:hover": {
                                        backgroundColor: selected
                                            ? "#fef2f2"
                                            : "rgba(255,255,255,0.12)",
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
                <TextField
                    multiline
                    minRows={3}
                    placeholder="Tell us how we can improve..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    fullWidth
                    variant="filled"
                    InputProps={{
                        sx: {
                            backgroundColor: "#fff",
                            borderRadius: 2,
                            "&:hover": {
                                backgroundColor: "#fff",
                            },
                            "&.Mui-focused": {
                                backgroundColor: "#fff",
                            },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 1,
                    justifyContent: "center",
                }}
            >
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{
                        backgroundColor: "#fff",
                        color: MAIN_COLOR_RED,
                        borderRadius: 999,
                        px: 4,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": { backgroundColor: "#fef2f2" },
                    }}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;

